import { useMemo, useState } from 'react'
import { TrendingUp } from 'lucide-react'
import { CartesianGrid, Line, LineChart, XAxis, YAxis } from 'recharts'
import {
  Bone,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  SegmentedControl,
} from '@/components/ui'
import { cn } from '@/lib/cn'
import type { OrganizationDashboard } from '@/types/dashboard.types'
import { DashboardDetailModal, DashboardTile } from './DashboardTile'
import { TREND_SERIES } from './chartSeries'
import { formatNumber, formatPct } from './dashboardFormat'

type TrendMetric = 'rate' | 'headcount'

interface TrendPoint {
  label: string
  thisWeek: number | null
  lastWeek: number | null
}

function trendPoints(data: OrganizationDashboard, metric: TrendMetric): TrendPoint[] {
  const last = data.lastWeekAttendance ?? []
  return data.weekAttendance.map((day, index) => {
    const prev = last[index]
    if (metric === 'rate') {
      return { label: day.label, thisWeek: day.presentPct, lastWeek: prev?.presentPct ?? null }
    }
    // A day still ahead has no headcount yet: a gap, not a zero.
    return {
      label: day.label,
      thisWeek: day.presentPct === null && day.present === 0 ? null : day.present,
      lastWeek: prev ? prev.present : null,
    }
  })
}

function TrendChart({
  points,
  metric,
  height,
}: {
  points: TrendPoint[]
  metric: TrendMetric
  height: string
}): React.ReactElement {
  const isRate = metric === 'rate'
  return (
    <ChartContainer config={TREND_SERIES} className={cn('aspect-auto w-full', height)}>
      <LineChart data={points} margin={{ top: 8, right: 12, bottom: 0, left: -4 }}>
        <CartesianGrid vertical={false} strokeDasharray="3 3" />
        <XAxis dataKey="label" tickLine={false} axisLine={false} tickMargin={6} />
        <YAxis
          tickLine={false}
          axisLine={false}
          width={44}
          allowDecimals={false}
          domain={isRate ? [0, 100] : [0, 'auto']}
          tickFormatter={isRate ? (v: number) => `${v}%` : undefined}
        />
        <ChartTooltip
          content={
            <ChartTooltipContent formatter={isRate ? (v) => `${v}%` : (v) => formatNumber(v)} />
          }
        />
        <Line
          dataKey="lastWeek"
          type="monotone"
          stroke="var(--color-lastWeek)"
          strokeWidth={1.75}
          strokeDasharray="5 4"
          dot={{ r: 2.5, fill: 'var(--color-lastWeek)', strokeWidth: 0 }}
          connectNulls={false}
        />
        <Line
          dataKey="thisWeek"
          type="monotone"
          stroke="var(--color-thisWeek)"
          strokeWidth={2.25}
          dot={{ r: 3, fill: 'var(--color-thisWeek)', strokeWidth: 0 }}
          activeDot={{ r: 5 }}
          connectNulls={false}
        />
      </LineChart>
    </ChartContainer>
  )
}

function TrendLegend({ hasLastWeek }: { hasLastWeek: boolean }): React.ReactElement {
  return (
    <div className="hidden items-center gap-3 text-xs text-gray-500 dark:text-gray-400 sm:flex">
      <span className="inline-flex items-center gap-1.5">
        <span className="h-0.5 w-3.5 rounded-full bg-primary dark:bg-[#4aa3c4]" aria-hidden />
        This week
      </span>
      {hasLastWeek ? (
        <span className="inline-flex items-center gap-1.5">
          <span className="h-0 w-3.5 border-t-2 border-dashed border-accent" aria-hidden />
          Last week
        </span>
      ) : null}
    </div>
  )
}

/** This week's attendance rate against the same days last week. */
export function AttendanceTrendsCard({
  data,
  isLoading,
  onExpand,
  className,
}: {
  data: OrganizationDashboard | undefined
  isLoading: boolean
  onExpand: () => void
  className?: string
}): React.ReactElement {
  const hasLastWeek = (data?.lastWeekAttendance?.length ?? 0) > 0
  // Memoized so re-renders (modal open, refetch) don't replay the line animation.
  const points = useMemo(() => (data ? trendPoints(data, 'rate') : []), [data])
  const hasData = points.some((p) => p.thisWeek !== null || p.lastWeek !== null)

  return (
    <DashboardTile
      title="Attendance trends"
      subtitle="Share of staff due who attended"
      icon={TrendingUp}
      onExpand={onExpand}
      className={className}
      actions={<TrendLegend hasLastWeek={hasLastWeek} />}
      bodyClassName="px-3 pb-3"
    >
      {isLoading ? (
        <Bone className="mx-2 h-52 rounded-lg" />
      ) : !hasData ? (
        <div className="grid h-52 place-items-center px-6 text-center text-sm text-gray-500 dark:text-gray-400">
          {data?.isLive === false
            ? 'No scheduled shifts were worked that week.'
            : 'The line starts once a scheduled shift has been worked this week.'}
        </div>
      ) : (
        <TrendChart points={points} metric="rate" height="h-52" />
      )}
    </DashboardTile>
  )
}

const METRIC_OPTIONS = [
  { value: 'rate' as const, label: 'Attendance rate' },
  { value: 'headcount' as const, label: 'People attended' },
]

export function AttendanceTrendsModal({
  open,
  data,
  onClose,
}: {
  open: boolean
  data: OrganizationDashboard | undefined
  onClose: () => void
}): React.ReactElement | null {
  const [metric, setMetric] = useState<TrendMetric>('rate')
  const points = useMemo(() => (data ? trendPoints(data, metric) : []), [data, metric])
  if (!open) return null
  const hasLastWeek = (data?.lastWeekAttendance?.length ?? 0) > 0
  const format = (value: number | null): string =>
    metric === 'rate' ? formatPct(value) : value === null ? '—' : formatNumber(value)

  return (
    <DashboardDetailModal
      open
      onClose={onClose}
      title="Attendance trends"
      subtitle={
        hasLastWeek
          ? 'This week against the same weekdays last week. Days still ahead are left blank.'
          : 'This week so far. Last week appears here once the server sends it.'
      }
      toolbar={
        <SegmentedControl
          options={METRIC_OPTIONS}
          value={metric}
          onChange={setMetric}
          ariaLabel="Trend measure"
          size="sm"
        />
      }
    >
      {!data ? (
        <Bone className="h-80 w-full rounded-lg" />
      ) : (
        <div className="space-y-6">
          <div className="flex justify-end">
            <TrendLegend hasLastWeek={hasLastWeek} />
          </div>
          <TrendChart points={points} metric={metric} height="h-72" />
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:bg-gray-900/40 dark:text-gray-400">
                <tr>
                  <th scope="col" className="px-4 py-2 text-left">
                    Day
                  </th>
                  <th scope="col" className="px-4 py-2 text-right">
                    This week
                  </th>
                  <th scope="col" className="px-4 py-2 text-right">
                    Last week
                  </th>
                  <th scope="col" className="px-4 py-2 text-right">
                    Change
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 tabular-nums dark:divide-gray-700/70">
                {points.map((point) => {
                  const change =
                    point.thisWeek !== null && point.lastWeek !== null
                      ? point.thisWeek - point.lastWeek
                      : null
                  return (
                    <tr key={point.label}>
                      <td className="px-4 py-2.5 font-medium text-gray-900 dark:text-gray-100">
                        {point.label}
                      </td>
                      <td className="px-4 py-2.5 text-right text-gray-900 dark:text-gray-100">
                        {format(point.thisWeek)}
                      </td>
                      <td className="px-4 py-2.5 text-right text-gray-500 dark:text-gray-400">
                        {format(point.lastWeek)}
                      </td>
                      <td
                        className={cn(
                          'px-4 py-2.5 text-right font-medium',
                          change === null || change === 0
                            ? 'text-gray-400 dark:text-gray-500'
                            : change > 0
                              ? 'text-emerald-700 dark:text-emerald-400'
                              : 'text-[#d92d50] dark:text-rose-300'
                        )}
                      >
                        {change === null
                          ? '—'
                          : `${change > 0 ? '+' : ''}${formatNumber(change)}${metric === 'rate' ? ' pts' : ''}`}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </DashboardDetailModal>
  )
}
